// ═══════════════════════════════════════════════════════════════════════════
// Episode-document exports
//
// Turns a unified episode `document` ({ title, hook, questions, script,
// talking_points, format, quality, generated_at }) into downloadable PDF
// and .docx files.
//
// Browser-only — both jsPDF and docx run client-side. No server round-trip.
// ═══════════════════════════════════════════════════════════════════════════

import { saveAs } from 'file-saver';
import { QUALITY_MODES } from './episodeBrief';

// Plain helper: safe filename. Avoids OS filename-hostile chars but keeps
// spaces (per the filename format the product spec calls for).
function filenameFor(title, ext) {
  const safe = (title || 'Untitled episode')
    .replace(/[\/\\?%*:|"<>]/g, '')
    .trim()
    .slice(0, 80);
  return `${safe} — Episode Brief.${ext}`;
}

function formattedDate(iso) {
  const d = iso ? new Date(iso) : new Date();
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

function qualityLabel(q) {
  return QUALITY_MODES[q]?.label || 'Standard';
}

// ─── PDF ──────────────────────────────────────────────────────────────────
export async function downloadAsPDF(doc) {
  const { jsPDF } = await import('jspdf');
  const pdf = new jsPDF({ unit: 'pt', format: 'letter' });

  const M = 54;                         // 0.75" margins
  const PAGE_W = pdf.internal.pageSize.getWidth();
  const PAGE_H = pdf.internal.pageSize.getHeight();
  const LINE_W = PAGE_W - (M * 2);
  let y = M;

  function ensureSpace(needed) {
    if (y + needed > PAGE_H - M) {
      pdf.addPage();
      y = M;
    }
  }

  function writeParagraph(text, { size = 11, leading = 15, bold = false, color = '#111' } = {}) {
    if (!text) return;
    pdf.setFont('helvetica', bold ? 'bold' : 'normal');
    pdf.setFontSize(size);
    pdf.setTextColor(color);
    const lines = pdf.splitTextToSize(text, LINE_W);
    for (const line of lines) {
      ensureSpace(leading);
      pdf.text(line, M, y);
      y += leading;
    }
  }

  function writeHeading(text, { size = 14, color = '#0c0808' } = {}) {
    ensureSpace(size + 14);
    y += 10;
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(size);
    pdf.setTextColor(color);
    pdf.text(text, M, y);
    y += size + 6;
    // Amber underline
    pdf.setDrawColor('#ffb101');
    pdf.setLineWidth(1.2);
    pdf.line(M, y - 2, M + 36, y - 2);
    y += 6;
  }

  // ── Header: title + generated-on + mode badge ────────────────────────────
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(22);
  pdf.setTextColor('#0c0808');
  const titleLines = pdf.splitTextToSize(doc.title || 'Untitled episode', LINE_W);
  for (const line of titleLines) {
    ensureSpace(26);
    pdf.text(line, M, y);
    y += 26;
  }
  y += 4;
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  pdf.setTextColor('#666');
  const meta = `Episode Brief  ·  Generated ${formattedDate(doc.generated_at)}  ·  ${qualityLabel(doc.quality)} quality`;
  pdf.text(meta, M, y);
  y += 16;

  // Divider
  pdf.setDrawColor('#e8e3da');
  pdf.line(M, y, PAGE_W - M, y);
  y += 18;

  // ── Hook ────────────────────────────────────────────────────────────────
  if (doc.hook) {
    writeHeading('Marketing Hook');
    writeParagraph(doc.hook, { size: 12, leading: 17 });
  }

  // ── Interview questions ─────────────────────────────────────────────────
  if (doc.questions?.length > 0) {
    writeHeading('Interview Questions');
    for (let i = 0; i < doc.questions.length; i++) {
      const q = doc.questions[i];
      writeParagraph(`${i + 1}. ${q}`, { size: 11, leading: 15 });
      y += 3;
    }
  }

  // ── Script or talking points ────────────────────────────────────────────
  const isInterview = doc.format === 'interview';
  if (isInterview && doc.talking_points?.length > 0) {
    writeHeading('Talking Points');
    for (const p of doc.talking_points) writeParagraph(p, { size: 11, leading: 15 });
  } else if (doc.script) {
    writeHeading('Script');
    // Render markdown headings as bold lines; body as normal paragraphs.
    const blocks = doc.script.split(/\n{2,}/);
    for (const block of blocks) {
      const trimmed = block.trim();
      if (!trimmed) continue;
      if (/^#{1,3}\s/.test(trimmed)) {
        const txt = trimmed.replace(/^#{1,3}\s*/, '');
        y += 6;
        writeParagraph(txt, { size: 12, leading: 16, bold: true });
      } else {
        writeParagraph(trimmed, { size: 11, leading: 15 });
        y += 4;
      }
    }
  }

  // Footer: Epio branding + page numbers
  const pageCount = pdf.internal.getNumberOfPages();
  for (let p = 1; p <= pageCount; p++) {
    pdf.setPage(p);
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    pdf.setTextColor('#aaa');
    pdf.text('Built with Epio', M, PAGE_H - 24);
    pdf.text(`${p} / ${pageCount}`, PAGE_W - M, PAGE_H - 24, { align: 'right' });
  }

  pdf.save(filenameFor(doc.title, 'pdf'));
}

// ─── DOCX ─────────────────────────────────────────────────────────────────
export async function downloadAsDocx(doc) {
  const {
    Document, Paragraph, TextRun, HeadingLevel, AlignmentType, Packer,
  } = await import('docx');

  const children = [];

  // Title
  children.push(new Paragraph({
    heading: HeadingLevel.TITLE,
    children: [new TextRun({ text: doc.title || 'Untitled episode', bold: true })],
  }));

  // Metadata line
  children.push(new Paragraph({
    alignment: AlignmentType.LEFT,
    spacing: { after: 240 },
    children: [new TextRun({
      text: `Episode Brief  ·  Generated ${formattedDate(doc.generated_at)}  ·  ${qualityLabel(doc.quality)} quality`,
      italics: true,
      color: '666666',
      size: 20,
    })],
  }));

  function heading(text) {
    children.push(new Paragraph({
      heading: HeadingLevel.HEADING_2,
      spacing: { before: 200, after: 120 },
      children: [new TextRun({ text, bold: true, color: '0c0808' })],
    }));
  }

  function body(text, opts = {}) {
    children.push(new Paragraph({
      spacing: { after: 120 },
      children: [new TextRun({ text, ...opts })],
    }));
  }

  function bullet(text) {
    children.push(new Paragraph({
      bullet: { level: 0 },
      spacing: { after: 80 },
      children: [new TextRun({ text })],
    }));
  }

  if (doc.hook) {
    heading('Marketing Hook');
    body(doc.hook);
  }

  if (doc.questions?.length > 0) {
    heading('Interview Questions');
    for (const q of doc.questions) bullet(q);
  }

  const isInterview = doc.format === 'interview';
  if (isInterview && doc.talking_points?.length > 0) {
    heading('Talking Points');
    for (const p of doc.talking_points) bullet(p);
  } else if (doc.script) {
    heading('Script');
    const blocks = doc.script.split(/\n{2,}/);
    for (const block of blocks) {
      const trimmed = block.trim();
      if (!trimmed) continue;
      if (/^#{1,3}\s/.test(trimmed)) {
        children.push(new Paragraph({
          heading: HeadingLevel.HEADING_3,
          spacing: { before: 200, after: 80 },
          children: [new TextRun({ text: trimmed.replace(/^#{1,3}\s*/, ''), bold: true })],
        }));
      } else {
        body(trimmed);
      }
    }
  }

  const document = new Document({
    creator: 'Epio',
    title: doc.title || 'Episode Brief',
    sections: [{ properties: {}, children }],
  });

  const blob = await Packer.toBlob(document);
  saveAs(blob, filenameFor(doc.title, 'docx'));
}

// ─── Google Docs (rich, formatted paste) ────────────────────────────────────
// Opening a blank Google Doc and pasting PLAIN text loses every heading and
// list. copyForGoogleDocs puts richly-formatted HTML on the clipboard, so a
// paste into Google Docs keeps the title, section headings, and bullets. Same
// document model as the .docx export so the two stay in sync.
function escHtml(s = '') {
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

export function docToHtml(doc) {
  const p = [`<h1>${escHtml(doc.title || 'Untitled episode')}</h1>`];
  p.push(`<p><em>Episode brief · Generated ${formattedDate(doc.generated_at)} · ${qualityLabel(doc.quality)} quality</em></p>`);
  if (doc.hook) p.push(`<h2>Marketing Hook</h2><p>${escHtml(doc.hook)}</p>`);
  if (doc.questions?.length) {
    p.push('<h2>Interview Questions</h2><ul>');
    for (const q of doc.questions) p.push(`<li>${escHtml(q)}</li>`);
    p.push('</ul>');
  }
  const isInterview = doc.format === 'interview';
  if (isInterview && doc.talking_points?.length) {
    p.push('<h2>Talking Points</h2><ul>');
    for (const t of doc.talking_points) p.push(`<li>${escHtml(t)}</li>`);
    p.push('</ul>');
  } else if (doc.script) {
    p.push('<h2>Script</h2>');
    for (const block of doc.script.split(/\n{2,}/)) {
      const t = block.trim();
      if (!t) continue;
      if (/^#{1,3}\s/.test(t)) p.push(`<h3>${escHtml(t.replace(/^#{1,3}\s*/, ''))}</h3>`);
      else p.push(`<p>${escHtml(t)}</p>`);
    }
  }
  return `<div>${p.join('\n')}</div>`;
}

function docToPlain(doc) {
  const L = [doc.title || 'Untitled episode', ''];
  if (doc.hook) L.push('MARKETING HOOK', doc.hook, '');
  if (doc.questions?.length) { L.push('INTERVIEW QUESTIONS'); doc.questions.forEach(q => L.push('• ' + q)); L.push(''); }
  const isInterview = doc.format === 'interview';
  if (isInterview && doc.talking_points?.length) { L.push('TALKING POINTS'); doc.talking_points.forEach(t => L.push('• ' + t)); L.push(''); }
  else if (doc.script) { L.push('SCRIPT', doc.script.replace(/^#{1,3}\s*/gm, '')); }
  return L.join('\n');
}

/** Copy the doc as rich HTML (plain-text fallback) so it pastes into Google
 *  Docs fully formatted. Returns true on success. */
export async function copyForGoogleDocs(doc) {
  const html = docToHtml(doc);
  const plain = docToPlain(doc);
  try {
    if (navigator.clipboard && window.ClipboardItem) {
      await navigator.clipboard.write([new window.ClipboardItem({
        'text/html':  new Blob([html],  { type: 'text/html' }),
        'text/plain': new Blob([plain], { type: 'text/plain' }),
      })]);
      return true;
    }
  } catch { /* fall through to plain-text copy */ }
  try { await navigator.clipboard.writeText(plain); return true; } catch { return false; }
}

// ─── Legacy adapter ────────────────────────────────────────────────────────
// Old episodes pre-v2 stored hooks[], run_of_show[], etc. across the
// `result` blob. Map those into the unified-document shape so the new view
// can render them without the legacy tab UI.
// Strip inline meta-tags like [PAUSE] / [HOOK] so they don't bleed
// through into the export or the rendered script.
function stripBeatTags(s) {
  return String(s || '').replace(/\[[^\]]+\]/g, ' ').replace(/\s+/g, ' ').trim();
}

// architectEpisode produces docs shaped as { kind:'episode', title,
// summary, beats[], seo, titleBreakdown, disclaimer, generatedAt }
// — NOT { hook, script, questions } like the older briefs the
// EpisodeDocumentView + downloadAsDocx/PDF know how to render.
// Translate the new shape into the canonical one so both the editor
// page and the export pipeline render the full body instead of just
// the title header.
function adaptArchitectDoc(doc, quality) {
  if (!doc || !Array.isArray(doc.beats)) return null;
  const script = doc.beats
    .map(b => {
      const label = b?.label || b?.heading || b?.title;
      const body  = stripBeatTags(b?.content || b?.text || b?.body || '');
      const head  = label ? `## ${label}` : '';
      return [head, body].filter(Boolean).join('\n\n');
    })
    .filter(Boolean)
    .join('\n\n');

  return {
    title:           doc.title || 'Untitled episode',
    hook:            doc.summary || '',
    questions:       [],
    script,
    talking_points: [],
    format:          doc.format || 'solo',
    quality:         quality || 'standard',
    model_used:      doc.model || null,
    generated_at:    doc.generatedAt || null,
    title_alternates: Array.isArray(doc.seo?.titles) ? doc.seo.titles : [],
    legacy:          false,
  };
}

export function adaptLegacyResult(result) {
  if (!result) return null;
  if (result.document) {
    const doc = result.document;
    // architectEpisode shape has `beats[]` and no `script` field; that
    // mismatch was making the editor + export render an empty body.
    if (Array.isArray(doc.beats) && !doc.script) {
      return adaptArchitectDoc(doc, result.quality);
    }
    return doc; // already in the canonical shape
  }

  const topHookIdx = result.top_hook ?? 0;
  const topTitleIdx = result.top_podcast_title ?? 0;
  const hook =
    result.hooks?.[topHookIdx]?.text ||
    result.hooks?.[0]?.text ||
    '';
  const title =
    result.podcast_titles?.[topTitleIdx]?.text ||
    result.youtube_titles?.[0]?.text ||
    'Untitled episode';

  const questions = Array.isArray(result.interview_guide)
    ? result.interview_guide
        .map(s => s.opening_question)
        .filter(Boolean)
    : [];

  const script = Array.isArray(result.run_of_show)
    ? result.run_of_show
        .map(s => {
          const head = s.segment ? `## ${s.segment}${s.duration ? ` (${s.duration})` : ''}` : '';
          const points = Array.isArray(s.talking_points) ? s.talking_points.join('\n') : '';
          return [head, points].filter(Boolean).join('\n');
        })
        .filter(Boolean)
        .join('\n\n')
    : (result.show_notes || '');

  return {
    title,
    hook,
    questions,
    script,
    talking_points: questions.length === 0 && result.show_notes ? [result.show_notes] : [],
    format: questions.length > 0 ? 'interview' : 'solo',
    quality: 'standard',
    model_used: null,
    generated_at: result.generated_at || null,
    legacy: true,
  };
}
